import type { StockMachineDataDto } from '@api/machines'
import {
  type MachineAttachmentItemDto,
  type MachineQuotationItemDto,
  MachinesApiService,
  type QuotationDto
} from '@api/machines'
import {
  ATTACHMENT_SUB_PATH,
  EQUIPMENT_SUB_PATH,
  GENERAL_MACHINE_TYPES,
  handleCatchError
} from '@common/utils'
import {
  MolsonBox,
  MolsonButton,
  MolsonDialog,
  MolsonDialogActions,
  MolsonDialogContent,
  MolsonDialogHeader,
  MolsonTypography
} from '@components/reusable'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useGetAvailableReplacements } from '@/shared-queries/machines'
import { ReplaceEquipmentGrid } from './ReplaceEquipmentGrid'

export type ReplaceEquipmentDialogProps = {
  closeDialog: () => void
  isDialogOpen: boolean
  quotationItem: MachineAttachmentItemDto & MachineQuotationItemDto
  quote: QuotationDto
}

export const ReplaceQuotationItemDialog = ({
  quote,
  closeDialog,
  isDialogOpen,
  quotationItem
}: ReplaceEquipmentDialogProps) => {
  const { t } = useTranslation('')
  const commonTranslationKey = 'common'
  const nonCommonTranslationKey =
    'newQuote.itemsTab.cards.machineCard.replaceMachineDialog'

  const { quoteId } = useParams()

  const navigate = useNavigate()

  const { data: availableReplacementsOriginal } = useGetAvailableReplacements({
    machineType: `${quotationItem.machineType}`,
    quotationItemdId: quotationItem.id,
    enabled: isDialogOpen
  })

  const [selectedReplacementId, setSelectedReplacementId] = useState<
    number | null
  >(null)

  const outOfStockItemData = useMemo(() => {
    const item: StockMachineDataDto[] = [
      {
        unitNumber: quotationItem.stockInfo?.unitNumber,
        status: quotationItem.stockInfo?.stockStatus,
        location: quotationItem.stockInfo?.currentLocation,
        daysInStock: 0,
        configuration: quotationItem.stockInfo?.optionText
      }
    ]

    return item
  }, [quotationItem])

  const availableReplacements = useMemo(() => {
    let quoteItems: MachineQuotationItemDto[] | MachineAttachmentItemDto[]

    if (quotationItem.machineType === GENERAL_MACHINE_TYPES.MACHINE) {
      quoteItems = quote.machineItems as MachineQuotationItemDto[]
    } else {
      quoteItems = quote.machineAttachmentItems as MachineAttachmentItemDto[]
    }

    const filteredList = availableReplacementsOriginal?.filter(
      replacement =>
        !quoteItems.some(
          quoteItem => quoteItem.stockInfo?.stockItemId === replacement.stockId
        )
    )

    return filteredList
  }, [availableReplacementsOriginal, quote, quotationItem.machineType])

  const isNestedAttachment = quote?.machineItems
    ?.flatMap(machineItem => machineItem.machineAttachments ?? [])
    .find(
      nestedAtt => nestedAtt.quotationItemId === quotationItem.quotationItemId
    )
  const parentMachine = quote?.machineItems?.find(machineItem =>
    machineItem.machineAttachments?.some(
      nestedAtt => nestedAtt.quotationItemId === quotationItem.quotationItemId
    )
  )

  const handleSelectReplacementItem = (itemId: number) => {
    setSelectedReplacementId(itemId)
  }

  const handleNextStep = async () => {
    const subPath =
      quotationItem.machineType === GENERAL_MACHINE_TYPES.MACHINE
        ? EQUIPMENT_SUB_PATH
        : ATTACHMENT_SUB_PATH

    if (selectedReplacementId) {
      try {
        const replacedItemId =
          await MachinesApiService.quotationReplaceStockItem(
            quoteId as string,
            quotationItem?.quotationItemId ?? '',
            selectedReplacementId
          )

        const navigationPath = isNestedAttachment
          ? `${EQUIPMENT_SUB_PATH}/${parentMachine?.quotationItemId}/${ATTACHMENT_SUB_PATH}/${replacedItemId}?edit=true`
          : `${subPath}/${replacedItemId}?edit=true`

        navigate(navigationPath, {
          state: {
            isReplacement: true
          }
        })
      } catch (error) {
        handleCatchError(error)
      }
    }
  }

  return (
    <MolsonDialog
      fullWidth={true}
      maxWidth="lg"
      onClose={closeDialog}
      open={isDialogOpen}
    >
      <MolsonDialogHeader
        subTitle={t(`${nonCommonTranslationKey}.subtitle`) || ''}
        title={t(`${nonCommonTranslationKey}.title`)}
      />

      <MolsonDialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
      >
        <MolsonBox>
          <MolsonTypography fontWeight="bold">
            {quotationItem.brand} {quotationItem.modelName} (
            {quotationItem.manufactureYear})
          </MolsonTypography>
          {[
            {
              primaryText: t(`${commonTranslationKey}.category`),
              secondaryText: quotationItem.category
            },
            {
              primaryText: t(`${commonTranslationKey}.stockType`),
              secondaryText: quotationItem.stockType
            }
          ].map(item => (
            <MolsonTypography
              fontWeight="bold"
              key={item.secondaryText}
              display="flex"
              gap={1}
              alignItems="center"
            >
              <span>{item.primaryText}:</span>
              <span style={{ fontWeight: 'normal' }}>{item.secondaryText}</span>
            </MolsonTypography>
          ))}
        </MolsonBox>

        <MolsonBox mb={4} mt={2}>
          <ReplaceEquipmentGrid
            showFilters={false}
            showPagination={false}
            showReplaceWithSuggestion={false}
            showSearchField={false}
            onRowClick={handleSelectReplacementItem}
            data={outOfStockItemData}
          />
        </MolsonBox>

        <MolsonBox>
          <ReplaceEquipmentGrid
            showFilters={true}
            showPagination={true}
            showSearchField={true}
            data={availableReplacements}
            onRowClick={handleSelectReplacementItem}
          />
        </MolsonBox>
      </MolsonDialogContent>

      <MolsonDialogActions>
        <MolsonButton
          color="secondary"
          onClick={closeDialog}
          variant="outlined"
        >
          {t(`${commonTranslationKey}.back`)}
        </MolsonButton>
        <MolsonButton
          disabled={!selectedReplacementId}
          onClick={handleNextStep}
          variant="contained"
        >
          {t(`${commonTranslationKey}.next`)}
        </MolsonButton>
      </MolsonDialogActions>
    </MolsonDialog>
  )
}
